"use client";

import React from "react";
import Image from "next/image";
import GlobalForm from "@/components/forms/GlobalForm";
import {
  SuccessMessageType,
  VerticalCategory,
  FinanceAndAccountingSubcategory,
  FormHeading,
} from "@/interface/types";
import { useInactivityPopup } from "@/hooks/useInactivityPopup";
import { useModalScrollLock } from "@/hooks/useModalScrollLock";

interface InactivityPopupProps {
  course?: string;
  courseId?: number;
  verticalName?: VerticalCategory;
  subCategory?: string;
  pageName?: string;
  delay?: number;
}

const InactivityPopup: React.FC<InactivityPopupProps> = ({
  course = "CMA",
  courseId = 2,
  verticalName = VerticalCategory.FINANCE_AND_ACCOUNTING,
  subCategory = FinanceAndAccountingSubcategory.CMA,
  pageName = "cma-visit",
  delay = 5000,
}) => {
  const { showPopup, hidePopup } = useInactivityPopup({
    delay,
    sessionStorageKey: `inactivity-popup-${pageName}`,
  });

  useModalScrollLock(showPopup, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  const handleClose = () => {
    // Clear both sessionStorage and localStorage entries
    sessionStorage.removeItem(`inactivity-popup-${pageName}`);
    sessionStorage.removeItem("user-interacted-form");
    localStorage.removeItem(`inactivity-popup-${pageName}`);
    hidePopup();
  };

  return (
    <>
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative overflow-hidden mx-auto border-[0.5px] border-white bg-[#061A2F] md:w-[640px] w-full"
            style={{
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={handleClose}
              className="text-white text-sm md:text-sm absolute right-2 top-2 md:right-2 md:top-2 cursor-pointer w-8 h-8 md:w-8 md:h-8 flex items-center justify-center rounded-full border border-white z-30"
            >
              ✕
            </button>

            <div className="flex gap-5 md:gap-10">
              <div className="relative z-10 p-6  md:w-[70%]">
                <GlobalForm
                  course={course}
                  courseId={courseId}
                  formHeading={
                    pageName === "cpa" || pageName === "cpa-visit"
                      ? FormHeading.CONNECT_WITH_A_CPA_EXPERT_FOR_DETAILS
                      : pageName === "cpa/cma"
                        ? FormHeading.CONNECT_WITH_A_CPA_CMA_EXPERT_FOR_DETAILS
                        : FormHeading.CONNECT_WITH_A_CMA_EXPERT_FOR_DETAILS
                  }
                  formType="Flash popup"
                  isAStepperForm={true}
                  pageType="accounting page"
                  subCategory={subCategory}
                  submitButtonName="Request a Call Back"
                  successMessage={SuccessMessageType.ACCOUNTING}
                  verticalName={verticalName}
                  darkBackground={true}
                />
              </div>
              <div className="w-[30%] hidden md:block">
                <Image
                  src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/eco-girl.webp"
                  alt="AI-Ready Accountant"
                  width={229}
                  height={316}
                  className="absolute right-0 bottom-0 z-0"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InactivityPopup;
